import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';


/** SoloKeys: ajustes limitados al panel del menu en celular. */
function installSoloKeysMobileMenuStyles() {
  if (document.getElementById('solokeys-mobile-menu-styles')) return;
  const style = document.createElement('style');
  style.id = 'solokeys-mobile-menu-styles';
  style.textContent = `
@media screen and (max-width: 749px) {
  #Details-menu-drawer-container {
    --drawer-animation-speed: 0.15s;
  }

  /* Quita el selector de mercado y el espacio sobrante del menu movil. */
  #Details-menu-drawer-container .menu-drawer__localization {
    display: none !important;
  }

  #Details-menu-drawer-container .menu-drawer__utility-links:not(:has(> :not(.menu-drawer__localization))) {
    display: none !important;
  }

  #Details-menu-drawer-container .menu-drawer__navigation {
    min-height: 0 !important;
  }

  #Details-menu-drawer-container .menu-drawer,
  #Details-menu-drawer-container .menu-drawer__submenu {
    overflow-x: hidden !important;
    overflow-y: auto !important;
    scrollbar-width: none !important;
  }

  #Details-menu-drawer-container .menu-drawer::-webkit-scrollbar,
  #Details-menu-drawer-container .menu-drawer__submenu::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
  }
  /* Todas las opciones aparecen juntas, sin espera por categoria. */
  #Details-menu-drawer-container[open] .menu-drawer__animated-element {
    animation: none !important;
    opacity: 1 !important;
    visibility: visible !important;
    transform: none !important;
  }

  #Details-menu-drawer-container .menu-drawer__navigation > .menu-drawer__menu {
    gap: 8px !important;
  }

  #Details-menu-drawer-container .menu-drawer__menu-item--mainlist {
    min-height: 64px !important;
    grid-template-columns: 40px minmax(0, 1fr) 16px !important;
    column-gap: 10px !important;
    padding: 9px 10px !important;
  }

  #Details-menu-drawer-container summary.menu-drawer__menu-item--mainlist {
    grid-template-columns: 40px minmax(0, 1fr) 30px !important;
  }

  #Details-menu-drawer-container .menu-drawer__collection-link {
    padding-inline-end: 48px !important;
  }

  #Details-menu-drawer-container .menu-drawer__collection-toggle {
    height: 64px;
    width: 44px;
  }

  #Details-menu-drawer-container .menu-drawer__menu-item--mainlist::before {
    width: 40px;
    height: 40px;
    background-size: 24px auto;
    border-radius: 11px;
    font-size: 20px;
  }

  #Details-menu-drawer-container #HeaderDrawer-autodesk::before {
    background-size: 28px auto;
  }

  #Details-menu-drawer-container .menu-drawer__menu-item--mainlist .menu-drawer__menu-item-text {
    min-width: 0;
    overflow: visible;
    white-space: normal;
    font-size: 15px !important;
    line-height: 1.25;
  }

  /* Nombres de programas debajo del titulo, como en la referencia. */
  #Details-menu-drawer-container .menu-drawer__menu-item--mainlist .menu-drawer__menu-item-text::after {
    display: block;
    margin-top: 3px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 11px;
    font-weight: 400;
    line-height: 1.35;
    letter-spacing: 0;
  }

  #Details-menu-drawer-container #HeaderDrawer-windows .menu-drawer__menu-item-text::after {
    content: "Windows 11 y 10 · Pro y Home";
  }
  #Details-menu-drawer-container #HeaderDrawer-office .menu-drawer__menu-item-text::after {
    content: "Microsoft 365 · Office 2024 y 2021";
  }
  #Details-menu-drawer-container #HeaderDrawer-autodesk .menu-drawer__menu-item-text::after {
    content: "AutoCAD · Civil 3D · Revit";
  }
  #Details-menu-drawer-container #HeaderDrawer-antivirus .menu-drawer__menu-item-text::after {
    content: "McAfee · ESET";
  }
  #Details-menu-drawer-container #HeaderDrawer-resenas-de-clientes .menu-drawer__menu-item-text::after {
    content: "Opiniones de nuestros clientes";
  }
  #Details-menu-drawer-container #HeaderDrawer-necesitas-soporte .menu-drawer__menu-item-text::after {
    content: "Contacta a nuestro equipo";
  }
  #Details-menu-drawer-container #HeaderDrawer-guias .menu-drawer__menu-item-text::after {
    content: "Tutoriales de instalación";
  }
}

`;
  document.head.appendChild(style);
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();
    installSoloKeysMobileMenuStyles();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
